import Button from '@/components/base/Button';

export default function HeroSection() {
  const stats = [
    { value: '99.9%', label: '服务可用性' },
    { value: '50%', label: '成本节省' },
    { value: '10倍', label: '性能提升' },
  ];

  return (
    <section className="relative min-h-[auto] lg:min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-100">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-20 -right-20 w-72 h-72 bg-blue-200 rounded-full opacity-40 blur-3xl"></div>
        <div className="absolute top-1/2 -left-20 w-80 h-80 bg-emerald-200 rounded-full opacity-30 blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-sky-200 rounded-full opacity-30 blur-3xl"></div>
        
        {/* Grid pattern */}
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(#2563eb 1px, transparent 1px), linear-gradient(90deg, #2563eb 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }}
        ></div>
      </div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-16 sm:py-20 lg:py-0">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-12 items-center">
          {/* Left Content */}
          <div className="text-center lg:text-left">
            <div className="inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-white/80 border border-blue-100 text-blue-600 text-xs sm:text-sm font-medium mb-4 sm:mb-6 shadow-sm">
              <i className="ri-cloud-line mr-1.5 sm:mr-2"></i>
              企业级 ARM 云计算平台
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
              <span className="block">ARM云</span>
              <span className="block text-blue-600 mt-1 sm:mt-2">智能云端生态</span>
              <span className="block text-xl sm:text-2xl lg:text-3xl xl:text-4xl text-gray-700 mt-3 sm:mt-4 font-medium">
                重构数字未来
              </span>
            </h1>
            
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 mb-6 sm:mb-8 max-w-2xl mx-auto lg:mx-0 leading-relaxed">
              基于ARM架构的新一代云计算平台，提供高性能、低功耗、高性价比的云服务解决方案，
              助力企业数字化转型，开启智能云端新时代
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto">
                <i className="ri-play-circle-line mr-2"></i>
                立即体验产品
              </Button>
              <Button variant="outline" size="lg" className="w-full sm:w-auto border-blue-600 text-blue-600 hover:bg-blue-50">
                <i className="ri-phone-line mr-2"></i>
                联系销售顾问
              </Button>
            </div>
            
            {/* Key Features */}
            <div className="mt-8 sm:mt-12 grid grid-cols-3 gap-3 sm:gap-6 max-w-md mx-auto lg:mx-0">
              {stats.map((stat, index) => (
                <div key={index} className="text-center p-3 bg-white/60 rounded-xl">
                  <div className="text-lg sm:text-2xl font-bold text-blue-600 mb-0.5 sm:mb-1">{stat.value}</div>
                  <div className="text-xs sm:text-sm text-gray-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Right Visual - Tech Illustration */}
          <div className="hidden lg:flex items-center justify-center">
            <div className="relative w-full max-w-lg">
              <div className="aspect-square rounded-3xl bg-gradient-to-br from-blue-500/10 to-emerald-500/10 border border-blue-100 p-8 flex items-center justify-center">
                <div className="relative w-full h-full">
                  {/* Central cloud */}
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-gradient-to-br from-blue-500 to-sky-400 rounded-3xl flex items-center justify-center shadow-xl">
                    <i className="ri-cloud-line text-6xl text-white"></i>
                  </div>
                  
                  {/* Orbiting elements */}
                  {[
                    { icon: 'ri-cpu-line', color: 'bg-emerald-500', top: '10%', left: '10%' },
                    { icon: 'ri-shield-check-line', color: 'bg-blue-500', top: '10%', right: '10%' },
                    { icon: 'ri-line-chart-line', color: 'bg-violet-500', bottom: '10%', left: '10%' },
                    { icon: 'ri-database-2-line', color: 'bg-sky-500', bottom: '10%', right: '10%' },
                  ].map((item, index) => (
                    <div
                      key={index}
                      className={`absolute ${item.color} w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg`}
                      style={{
                        top: item.top,
                        left: item.left,
                        right: item.right,
                        bottom: item.bottom,
                      }}
                    >
                      <i className={`${item.icon} text-2xl text-white`}></i>
                    </div>
                  ))}
                  
                  {/* Connection lines */}
                  <svg className="absolute inset-0 w-full h-full" style={{ zIndex: -1 }}>
                    <line x1="50%" y1="50%" x2="17%" y2="17%" stroke="#93c5fd" strokeWidth="2" strokeDasharray="6 4" />
                    <line x1="50%" y1="50%" x2="83%" y2="17%" stroke="#93c5fd" strokeWidth="2" strokeDasharray="6 4" />
                    <line x1="50%" y1="50%" x2="17%" y2="83%" stroke="#93c5fd" strokeWidth="2" strokeDasharray="6 4" />
                    <line x1="50%" y1="50%" x2="83%" y2="83%" stroke="#93c5fd" strokeWidth="2" strokeDasharray="6 4" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
